// @ts-nocheck
import { CURVE_ORDER } from "./constants.js";
import { InvalidSignatureError } from "./errors.js";

/**
 * Convert 32-byte big-endian array to bigint
 * @param {Uint8Array} bytes
 * @returns {bigint}
 * @throws {InvalidSignatureError} If bytes is not 32 bytes
 */
function bytes32ToBigInt(bytes) {
	if (bytes.length !== 32) {
		throw new InvalidSignatureError(`Expected 32 bytes, got ${bytes.length}`, {
			code: "SECP256K1_INVALID_SIGNATURE_COMPONENT_LENGTH",
			context: { length: bytes.length, expected: 32 },
			docsPath: "/crypto/secp256k1#error-handling",
		});
	}
	let result = 0n;
	for (let i = 0; i < 32; i++) {
		result = (result << 8n) | BigInt(bytes[i] ?? 0);
	}
	return result;
}

/**
 * Validate signature components
 *
 * Checks that r and s are within valid range [1, n-1] where n is the
 * curve order. Also enforces low-s values to prevent malleability.
 *
 * @see https://voltaire.tevm.sh/crypto for crypto documentation
 * @since 0.0.0
 * @param {import('./SignatureType.js').Secp256k1SignatureType} signature - ECDSA signature to validate (r and s are HashType)
 * @returns {boolean} true if signature is valid, false otherwise
 * @throws {never}
 * @example
 * ```javascript
 * import * as Secp256k1 from './crypto/Secp256k1/index.js';
 * import * as Hash from './primitives/Hash/index.js';
 * const signature = { r: Hash.from(new Uint8Array(32)), s: Hash.from(new Uint8Array(32)), v: 27 };
 * const valid = Secp256k1.isValidSignature(signature);
 * ```
 */
export function isValidSignature(signature) {
	try {
		const r = bytes32ToBigInt(signature.r);
		const s = bytes32ToBigInt(signature.s);

		// r and s must be in [1, n-1]
		if (r === 0n || r >= CURVE_ORDER) return false;
		if (s === 0n || s >= CURVE_ORDER) return false;

		// Ethereum enforces s <= n/2 to prevent malleability
		const halfN = CURVE_ORDER / 2n;
		if (s > halfN) return false;

		// v must be 0, 1, 27, or 28
		if (
			signature.v !== 0 &&
			signature.v !== 1 &&
			signature.v !== 27 &&
			signature.v !== 28
		) {
			return false;
		}

		return true;
	} catch {
		return false;
	}
}
